/*
 * @Author: Zhouqi
 * @Date: 2025-12-10 10:20:56
 * @LastEditors: Ryan
 * @LastEditTime: 2026-02-03
 */
import dynamic from 'next/dynamic';
import React from 'react';

import AIRemovalSkeleton from '@/3.0/ai-removal/ai-removal-skeleton';
import { Introduction } from '@/components/container';
import DelayedRender from '@/components/DelayRender';
import Footer from '@/components/Footer';
import Layout from '@/components/layout';
import SEOHead from '@/landing/components/SEOHead';
import { createSoftwareAppSchema } from '@/landing/utils/structuredData';

// 预加载首屏图片资源（第一组对比图）
const PRELOAD_IMAGES = [
  'https://assets.falcocut.ai/ai-removal/haircare-product-logo-remover-before.webp',
  'https://assets.falcocut.ai/ai-removal/haircare-product-logo-remover-after.webp',
];

// 动态加载完整的 AIRemoval
const AIRemoval = dynamic(() => import('@/3.0/ai-removal/entry'), {
  ssr: false,
  loading: () => (
    <DelayedRender>
      <AIRemovalSkeleton />
    </DelayedRender>
  ),
});

// 动态加载 SEO 内容
const SEOSections = dynamic(() => import('@/3.0/ai-removal/seo'), {
  ssr: true,
});

// SEO 常量
const PAGE_TITLE =
  'AI Subtitle Remover - Remove Subtitles, Watermarks & Logos from Videos | FalcoCut';
const META_DESCRIPTION =
  'Remove subtitles, watermarks, logos, and unwanted text from videos and images with AI. Clean up your content instantly with professional results. Free online removal tool.';
const CURRENT_URL = 'https://falcocut.ai/video-subtitle-remover';

// 结构化数据
const structuredData = createSoftwareAppSchema({
  name: 'FalcoCut AI Subtitle Remover',
  description: META_DESCRIPTION,
  url: CURRENT_URL,
  ratingValue: '4.8',
  ratingCount: '2800',
  featureList: [
    'AI-powered subtitle removal',
    'Watermark removal from videos',
    'Logo removal from images',
    'Text overlay removal',
    'Batch processing support',
    'High-quality output',
  ],
});

const AIRemovalPage: React.FC & {
  getLayout?: (page: React.ReactElement) => React.ReactNode;
  getExtraContent?: () => React.ReactNode;
} = () => {
  return (
    <>
      <SEOHead
        title={PAGE_TITLE}
        description={META_DESCRIPTION}
        url={CURRENT_URL}
        imageAlt="FalcoCut AI Subtitle Remover"
        publishedTime="2025-12-10"
        modifiedTime="2026-02-03"
        structuredData={structuredData}
        preloadImages={PRELOAD_IMAGES}
      />

      {/* 客户端动态加载完整组件（保留骨架屏） */}
      <AIRemoval />
    </>
  );
};

// 定义 getLayout 为直接返回页面本身，使用 Layout
AIRemovalPage.getLayout = (page: React.ReactElement) => <Layout>{page}</Layout>;

// 定义 SEO 部分和 Footer 作为额外内容，在 Layout 外渲染
AIRemovalPage.getExtraContent = () => {
  return (
    <Introduction>
      <SEOSections />
      <Footer />
    </Introduction>
  );
};

export const getStaticProps = async () => {
  return {
    props: {},
  };
};

export default AIRemovalPage;
